/**
 * Shared feature-key → friendly display-name map for AI usage features.
 *
 * Single source of truth for every screen that shows raw AI-usage feature
 * keys (mobile AI Usage screen, web usage breakdown, admin billing), so
 * labels never drift between surfaces.
 *
 * Hand-written (not generated) — safe from codegen regeneration, which only
 * touches src/generated.
 */

export const FEATURE_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  ai_coach: "AI Coach",
  ai_coach_analysis: "AI Coach Analysis",
  ai_coach_ask: "AI Coach Questions",
  ai_create: "AI Create",
  assistants: "Assistants",
  calendar_ai_chat: "Calendar AI Chat",
  community: "Community",
  contact_flex_rule_builder: "Contact Flex Rule Builder",
  goal_simulation_analyze: "Goal Simulation Analysis",
  goal_simulation_chat: "Goal Simulation Chat",
  goal_suggestions: "Goal Suggestions",
  journal_analyze: "Journal Analysis",
  journal_extract_tasks: "Journal Task Extraction",
  journal_insights: "Journal Insights",
  journal_writing_assist: "Journal Writing Assist",
  meditation_tts: "Meditation Voice",
  notepad_generate_plan: "Notepad Plan Generation",
  notepad_research_claude: "Notepad Research",
  phone_number_monthly: "Phone Number (Monthly)",
  retell_voice_call: "Voice Calls",
  sop_builder: "SOP Builder",
  suggestion_auto_group: "Suggestion Auto-Grouping",
  suggestion_similarity: "Suggestion Similarity",
  time_budget_allocation_preview: "Time Budget Allocation Preview",
  time_budget_analyze: "Time Budget Analysis",
  time_budget_goal_allocation: "Time Budget Goal Allocation",
  zeus_guardrail_interview: "Zeus Guardrail Interview",
  zeus_identity_interview: "Zeus Identity Interview",
  zeus_premium_tts: "Zeus Premium Voice",
  zeus_rule_builder: "Zeus Rule Builder",
  zeus_talk: "Zeus Talk",
};

/**
 * Fallback formatter for feature keys that are not in the map:
 * splits on `_`/`-`, capitalizes each word, and upper-cases "ai".
 */
function prettifyFeatureKey(feature: string): string {
  return feature
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) =>
      word.toLowerCase() === "ai"
        ? "AI"
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

/**
 * Returns the friendly display name for an AI-usage feature key.
 * Unknown keys fall back to a generic prettified form.
 */
export function formatFeatureName(feature: string): string {
  return FEATURE_DISPLAY_NAMES[feature] ?? prettifyFeatureKey(feature);
}
